import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ArrowLeft, ArrowRight, RefreshCw } from "lucide-react";

import { useCategory } from "../Services/useCategory";
import { useGlobalContext } from "../Context/GlobalContext";

const CATEGORY_BATCH_SIZE = 10;
const CATEGORY_API_LIMIT = 1000;

export default function CategorySection() {
  const sliderRef = useRef(null);
  const apiCalledRef = useRef(false);
  const loadFrameRef = useRef(null);

const { user, selectedCategoryId, selectCategory } = useGlobalContext();

  const { categories, getCategories, categoryLoading, categoryError } =
    useCategory();

    const userFirstName = useMemo(() => {
      const contextFirstName = String(
        user?.first_name || user?.firstName || "",
      ).trim();

      if (contextFirstName) {
        return contextFirstName;
      }

      try {
        const directFirstName = String(
          sessionStorage.getItem("first_name") || "",
        ).trim();

        if (directFirstName) {
          return directFirstName;
        }

        const storedUser = sessionStorage.getItem("chaafoUser");

        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);

          return String(
            parsedUser?.first_name || parsedUser?.firstName || "",
          ).trim();
        }
      } catch (error) {
        console.error("Unable to read user name:", error);
      }

      return "";
    }, [user]);

  const [visibleCount, setVisibleCount] = useState(CATEGORY_BATCH_SIZE);

  const fetchCategories = useCallback(async () => {
    setVisibleCount(CATEGORY_BATCH_SIZE);

    await getCategories({
      limit: CATEGORY_API_LIMIT,
      offset: 0,
    });
  }, [getCategories]);

  useEffect(() => {
    if (apiCalledRef.current) {
      return;
    }

    apiCalledRef.current = true;

    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    return () => {
      if (loadFrameRef.current) {
        window.cancelAnimationFrame(loadFrameRef.current);
      }
    };
  }, []);

const visibleCategories = useMemo(() => {
  if (!Array.isArray(categories)) {
    return [];
  }

  return [...categories]
    .sort((a, b) => {
      const orderA = Number(a?.sort_order) || 999999;
      const orderB = Number(b?.sort_order) || 999999;

      return orderA - orderB;
    })
    .slice(0, visibleCount);
}, [categories, visibleCount]);

  const hasMoreCategories = visibleCount < categories.length;

  const getScrollAmount = useCallback(() => {
    const slider = sliderRef.current;

    if (!slider) {
      return 0;
    }

    if (window.innerWidth < 640) {
      return slider.clientWidth * 0.82;
    }

    return slider.clientWidth * 0.72;
  }, []);

  const scrollLeft = () => {
    const slider = sliderRef.current;

    if (!slider) {
      return;
    }

    slider.scrollBy({
      left: -getScrollAmount(),
      behavior: "smooth",
    });
  };

  const scrollRight = () => {
    const slider = sliderRef.current;

    if (!slider) {
      return;
    }

    const isNearCurrentEnd =
      slider.scrollLeft + slider.clientWidth >= slider.scrollWidth - 80;

    if (isNearCurrentEnd && hasMoreCategories) {
      const previousScrollWidth = slider.scrollWidth;

      setVisibleCount((previous) =>
        Math.min(previous + CATEGORY_BATCH_SIZE, categories.length),
      );

      loadFrameRef.current = window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          const updatedSlider = sliderRef.current;

          if (!updatedSlider) {
            return;
          }

          const addedWidth = updatedSlider.scrollWidth - previousScrollWidth;

          updatedSlider.scrollBy({
            left:
              addedWidth > 0
                ? Math.min(addedWidth, getScrollAmount())
                : getScrollAmount(),
            behavior: "smooth",
          });
        });
      });

      return;
    }

    slider.scrollBy({
      left: getScrollAmount(),
      behavior: "smooth",
    });
  };

  const scrollToAllRestaurants = () => {
    window.setTimeout(() => {
      document.getElementById("all-restaurants")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };
const handleCategoryClick = (category) => {
  const categoryId = Number(category?.pk_category_id);
  const categoryName = String(category?.category_name || "").trim();

  if (!Number.isFinite(categoryId)) {
    return;
  }

  selectCategory(categoryId, categoryName);

  scrollToAllRestaurants();
};

  return (
    <section className="w-full overflow-hidden bg-blue-50 py-0 sm:py-5">
      <div className="mx-auto w-full max-w-[1350px] px-4 sm:px-5 md:px-8">
        {/* Header */}
        <div className="mb-5 flex items-start justify-between gap-4 sm:mb-6 sm:items-center sm:gap-4">
          <div className="min-w-0">
            <h2 className="text-[24px] font-semibold leading-tight tracking-[-0.6px] text-[#1f2937] sm:text-[29px] md:text-[36px]">
              {userFirstName ? (
                <>
                  <span className="text-[#21195A]">{userFirstName},</span>{" "}
                  What&apos;s on your mind?
                </>
              ) : (
                <>What&apos;s on your mind?</>
              )}
            </h2>

            <p className="mt-1 text-[12px] leading-5 text-[#91a2ba] sm:text-[15px]">
              Select a category to view restaurants
            </p>
          </div>

          {/* Top arrows */}
          {!categoryLoading &&
            !categoryError &&
            visibleCategories.length > 0 && (
              <div className="flex shrink-0 items-center gap-1.5 sm:gap-2 sm:pl-2 md:pl-6">
                <button
                  type="button"
                  onClick={scrollLeft}
                  aria-label="Previous categories"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-[#e3e8ef] bg-white text-[#94a6bd] shadow-sm transition hover:border-[#b9c3d0] hover:text-[#111827] active:scale-95 sm:h-11 sm:w-11"
                >
                  <ArrowLeft size={20} className="sm:h-[21px] sm:w-[21px]" />
                </button>

                <button
                  type="button"
                  onClick={scrollRight}
                  aria-label={
                    hasMoreCategories
                      ? "Load more categories"
                      : "Next categories"
                  }
                  className="relative flex h-10 w-10 items-center justify-center rounded-full border border-[#e3e8ef] bg-white text-[#94a6bd] shadow-sm transition hover:border-[#b9c3d0] hover:text-[#111827] active:scale-95 sm:h-11 sm:w-11"
                >
                  <ArrowRight size={20} className="sm:h-[21px] sm:w-[21px]" />
                </button>
              </div>
            )}
        </div>

        {/* Loading */}
        {categoryLoading && (
          <div className="flex gap-3 overflow-hidden sm:gap-5">
            {Array.from({
              length: CATEGORY_BATCH_SIZE,
            }).map((_, index) => (
              <div
                key={`category-skeleton-${index}`}
                className="w-[82px] shrink-0 sm:w-[105px]"
              >
                <div className="mx-auto h-[76px] w-[76px] animate-pulse rounded-full bg-[#e4e9ef] sm:h-[105px] sm:w-[105px]" />

                <div className="mx-auto mt-2.5 h-3.5 w-[65px] animate-pulse rounded bg-[#e4e9ef] sm:mt-3 sm:h-4 sm:w-[70px]" />
              </div>
            ))}
          </div>
        )}

        {/* Error */}
        {!categoryLoading && categoryError && (
          <div className="flex min-h-[180px] flex-col items-center justify-center rounded-2xl border border-dashed border-[#d7dde5] bg-white px-5 text-center">
            <p className="text-[14px] text-[#8491a4]">{categoryError}</p>

            <button
              type="button"
              onClick={() => {
                apiCalledRef.current = true;

                fetchCategories();
              }}
              className="mt-4 flex items-center gap-2 rounded-lg bg-[#111827] px-5 py-2.5 text-[13px] font-medium text-white"
            >
              <RefreshCw size={16} />
              Try again
            </button>
          </div>
        )}

        {/* Category slider */}
        {!categoryLoading && !categoryError && visibleCategories.length > 0 && (
          <div
            ref={sliderRef}
            className="
  flex
  w-full
  touch-pan-x
  snap-x
  snap-mandatory
  gap-3

  overflow-x-auto
  overscroll-x-contain
  scroll-smooth

  px-0.5
  pb-3

  [scrollbar-width:none]
  [-ms-overflow-style:none]
  [&::-webkit-scrollbar]:hidden
"
            style={{
              WebkitOverflowScrolling: "touch",
            }}
          >
            {visibleCategories.map((category) => {
              const categoryId = Number(category?.pk_category_id);

              const isSelected =
                selectedCategoryId !== null &&
                Number(selectedCategoryId) === categoryId;

              return (
                <button
                  key={categoryId || category?.category_name}
                  type="button"
                  onClick={() => handleCategoryClick(category)}
                  className="category-card group w-[82px] shrink-0 snap-start cursor-pointer sm:w-[105px]"
                >
                  <div
                    className={`relative mx-auto h-[72px] w-[72px] overflow-hidden rounded-full border-[3px] bg-white transition sm:h-[105px] sm:w-[105px] ${
                      isSelected
                        ? "border-[#111827] shadow-md"
                        : "border-transparent group-hover:border-[#d6dce4]"
                    }`}
                  >
                    <img
                      src={category?.category_image}
                      alt={category?.category_name || "Category"}
                      loading="lazy"
                      draggable={false}
                      className="category-image block h-full w-full select-none object-cover"
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                    />
                  </div>

                  <p
                    title={category?.category_name}
                    className={`mt-2.5 truncate text-center text-[12px] font-medium sm:mt-3 sm:text-[14px] ${
                      isSelected ? "text-[#111827]" : "text-[#555]"
                    }`}
                  >
                    {category?.category_name || "Category"}
                  </p>
                </button>
              );
            })}
          </div>
        )}

        {/* Empty */}
        {!categoryLoading && !categoryError && categories.length === 0 && (
          <div className="flex min-h-[180px] items-center justify-center rounded-2xl border border-dashed border-[#d7dde5] bg-white">
            <p className="text-[14px] text-[#8491a4]">No categories found.</p>
          </div>
        )}
      </div>

      <style>{`
        .category-image {
          transform: scale(1);
          will-change: transform;
        }

        .category-card:hover .category-image {
          animation: category-image-zoom-once 700ms ease-in-out 1;
        }

        @keyframes category-image-zoom-once {
          0% {
            transform: scale(1);
          }

          50% {
            transform: scale(1.1);
          }

          100% {
            transform: scale(1);
          }
        }

        @media (hover: none) {
          .category-card:active .category-image {
            animation: category-image-zoom-once 500ms ease-in-out 1;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .category-card:hover .category-image,
          .category-card:active .category-image {
            animation: none;
          }
        }
      `}</style>
    </section>
  );
}
